import React, { useState, useMemo, useEffect } from 'react';
import { Card, Button, ButtonGroup, Dropdown, Form, Spinner } from 'react-bootstrap';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../../contexts/ThemeContext';
import useIsMobile from '../../../hooks/useIsMobile';
import { getRuleDisplayName } from '../../../constants/ruleTypeDefinitions';
import { getRuleTypeColor, getRuleTypeOrder } from '../../../utils/tradingRulesRuleTypeColors';
import {
  aggregateViolationsOverTime,
  fillViolationBuckets,
  formatViolationBucketLabel,
} from '../../../utils/tradingRulesViolationBuckets';
import { isPlanDeviationRuleType } from '../../../utils/violationLimitActual';
import ViolationsTable from '../ViolationsTable';

/** Short chart-legend labels. Tooltips keep the full short name. */
function bucketMagnitude(row) {
  if (!row || typeof row !== 'object') return 0;
  const total = Number(row.total);
  if (Number.isFinite(total) && total > 0) return total;
  return Object.keys(row).reduce((sum, key) => {
    if (key === 'date' || key === 'timestamp' || key === 'total') return sum;
    const value = Number(row[key]);
    return sum + (Number.isFinite(value) && value > 0 ? value : 0);
  }, 0);
}

function legendLabel(ruleType) {
  const short = getRuleDisplayName(ruleType, true);
  if (short === 'Traded outside trading hours') return 'Outside Hours';
  if (short === 'Hit Daily Profit Target') return 'Hit Profit Target';
  if (short === 'Hit Max Risk Per Trade') return 'Hit Max Risk';
  if (short === 'Hit Max Position Loss') return 'Hit Position Loss';
  return short;
}

function ChartLegend({ items, colors, isMobile }) {
  if (!items?.length) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '3px 8px' : '4px 12px',
        paddingTop: isMobile ? 8 : 12,
        lineHeight: 1.15,
      }}
    >
      {items.map((item) => (
        <span
          key={item.key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            maxWidth: '100%',
            fontSize: isMobile ? '0.6875rem' : '0.75rem',
            color: colors.textMuted,
            whiteSpace: 'nowrap',
          }}
        >
          <span
            style={{
              width: isMobile ? 6 : 8,
              height: isMobile ? 6 : 8,
              borderRadius: 1,
              backgroundColor: item.color,
              flexShrink: 0,
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/**
 * ViolationsOverTimeChart - Displays rule violations over time
 * 
 * Simple line chart showing violations over the selected time range
 * Can display total violations or separated by rule type
 * Matches dashboard theme with professional, minimal styling
 */
const ViolationsOverTimeChart = ({
  data,
  timeRange,
  loading = false,
  violations = [],
  onFilterChange,
  selectedRuleType = 'all',
  accountId = null,
  onViolationDeleted,
  periodLabel = 'this period',
  hideTable = false,
  tableOnly = false,
  embedded = false,
  showPlanDeviations = false,
  onShowPlanDeviationsChange,
  defaultTableExpanded = true,
  aside = null,
}) => {
  const { colors } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const [viewMode, setViewMode] = useState('byType'); // 'total' or 'byType'
  const [chartStyle, setChartStyle] = useState('bar'); // 'line' or 'bar'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(defaultTableExpanded);
  const chartHeight = embedded ? 160 : (isMobile ? 168 : 340);
  const emptyStateHeight = embedded ? 120 : (isMobile ? 140 : (aside ? 220 : 340));

  // Plot the same violation rows the table uses, bucketed for this timeframe.
  // Pre-aggregated `data` is only a fallback when rows are already bucketed.
  const chartSource = useMemo(() => {
    const rows = Array.isArray(violations) ? violations : [];
    const hasRawRows = rows.some((row) => row?.violation_timestamp && row?.rule_type);
    if (hasRawRows) {
      return aggregateViolationsOverTime(rows, timeRange, {
        includePlanDeviations: showPlanDeviations,
      });
    }
    return Array.isArray(data) ? data : [];
  }, [data, showPlanDeviations, timeRange, violations]);

  // Fill missing buckets in ET so "today" matches trading-rules day cutoff.
  // If the filled axis drops every count (key mismatch), plot the source points.
  const filledData = useMemo(() => {
    const filled = fillViolationBuckets(chartSource, timeRange);
    const filledCount = filled.reduce((sum, row) => sum + bucketMagnitude(row), 0);
    const sourceCount = chartSource.reduce((sum, row) => sum + bucketMagnitude(row), 0);
    if (sourceCount > 0 && filledCount === 0) return chartSource;
    return filled;
  }, [chartSource, timeRange]);

  // Get all rule types that have data, sorted by color order for stacking
  const ruleTypesWithData = useMemo(() => {
    if (!filledData || filledData.length === 0) return [];

    const seen = new Set();
    filledData.forEach((day) => {
      Object.keys(day).forEach((key) => {
        if (key === 'date' || key === 'timestamp' || key === 'total') return;
        if (Number(day[key]) > 0) seen.add(key);
      });
    });

    return Array.from(seen)
      .filter((ruleType) => selectedRuleType === 'all' || ruleType === selectedRuleType)
      .sort((a, b) => getRuleTypeOrder(a) - getRuleTypeOrder(b));
  }, [filledData, selectedRuleType]);

  // Get unique rule types from violations for filter dropdown
  const availableRuleTypes = useMemo(() => {
    const types = new Set();
    violations.forEach(v => {
      if (!v.rule_type) return;
      if (!showPlanDeviations && isPlanDeviationRuleType(v.rule_type)) return;
      if (v.rule_type) {
        types.add(v.rule_type);
      }
    });
    return Array.from(types).sort();
  }, [showPlanDeviations, violations]);

  useEffect(() => {
    if (selectedRuleType === 'all') return;
    if (showPlanDeviations) return;
    if (isPlanDeviationRuleType(selectedRuleType) && onFilterChange) {
      onFilterChange('all');
    }
  }, [onFilterChange, selectedRuleType, showPlanDeviations]);

  // Create aggregated data for total view
  const aggregatedData = useMemo(() => {
    if (!filledData || filledData.length === 0) return [];
    
    return filledData.map(day => {
      const total = ruleTypesWithData.reduce((sum, ruleType) => {
        return sum + (day[ruleType] || 0);
      }, 0);
      
      return {
        // API returns 'timestamp', support both 'date' and 'timestamp' keys
        date: day.date || day.timestamp,
        total: total
      };
    });
  }, [filledData, ruleTypesWithData]);

  // Normalize data to always have 'date' key (API returns 'timestamp')
  const normalizedData = useMemo(() => {
    if (!filledData || filledData.length === 0) return [];
    return filledData.map(item => ({
      ...item,
      date: item.date || item.timestamp
    }));
  }, [filledData]);

  // Determine which data to use based on view mode
  const chartData = viewMode === 'total' ? aggregatedData : normalizedData;
  const totalSeriesLabel = showPlanDeviations ? 'Total Events' : 'Total Violations';

  const legendItems = useMemo(() => {
    if (viewMode === 'total') {
      return [{ key: 'total', label: totalSeriesLabel, color: colors.primary }];
    }
    return ruleTypesWithData.map((ruleType) => ({
      key: ruleType,
      label: legendLabel(ruleType),
      color: getRuleTypeColor(ruleType, colors),
    }));
  }, [colors, ruleTypesWithData, totalSeriesLabel, viewMode]);

  // Format date for display in Eastern Time (trading-rules day)
  const formatDate = (dateStr) => {
    return formatViolationBucketLabel(dateStr, timeRange, chartData?.length || 0);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '8px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <div style={{ 
            fontSize: '0.75rem', 
            color: colors.textMuted, 
            marginBottom: '4px',
            fontWeight: 500
          }}>
            {formatDate(label)}
          </div>
          {payload.map((entry, index) => (
            <div key={index} style={{ 
              fontSize: '0.75rem',
              color: colors.text,
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: entry.color,
                flexShrink: 0
              }} />
              <span>{entry.dataKey === 'total' ? totalSeriesLabel : getRuleDisplayName(entry.dataKey, true)}:</span>
              <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Empty only when nothing in this range is chartable. Rows that are excluded
  // from the chart (milestones, plan deviations) still render in the table.
  const hasNoViolations = useMemo(
    () => filledData.reduce((sum, row) => sum + bucketMagnitude(row), 0) === 0,
    [filledData]
  );

  // Get time range label for empty state message
  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today':
        return 'today';
      case '7days':
      case 'week':
        return 'last 7 days';
      case '30days':
      case 'month':
        return 'last 30 days';
      case 'quarter':
        return 'last 90 days';
      case 'ytd':
        return 'year to date';
      case 'year':
        return 'last year';
      case 'all':
        return 'all time';
      default:
        return periodLabel || 'this period';
    }
  };

  // tableOnly mode: just render the violations table in a card
  if (tableOnly) {
    if (!violations || violations.length === 0) return null;
    return (
      <Card style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: '8px' }}>
        <Card.Body style={{ padding: '20px' }}>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 500, marginBottom: '16px' }}>
            Violations
          </div>
          <ViolationsTable
            violations={violations}
            accountId={accountId}
            selectedRuleType={selectedRuleType}
            noCardWrapper={true}
            onViolationDeleted={onViolationDeleted}
          />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card style={{
      backgroundColor: colors.bgCard,
      border: embedded ? 'none' : `1px solid ${colors.border}`,
      borderRadius: embedded ? 0 : '8px',
      width: embedded ? '100%' : undefined,
    }}>
      <Card.Body style={{ padding: embedded ? 0 : '20px' }}>
        {/* Header: title + italic filters toggle */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: filtersOpen ? '10px' : (embedded ? '12px' : '20px'),
            gap: '12px',
          }}
        >
          <div
            style={{
              fontSize: '0.75rem',
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 500,
            }}
          >
            Violations Over Time
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              fontSize: '0.8125rem',
              fontStyle: 'italic',
              color: filtersOpen ? colors.primary : colors.textMuted,
              cursor: 'pointer',
              textDecoration: filtersOpen ? 'underline' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            filters
          </button>
        </div>

        {filtersOpen && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '10px 12px',
              marginBottom: embedded ? '12px' : '16px',
              padding: '12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgSecondary || colors.bgInput || 'transparent',
            }}
          >
            {!tableOnly && typeof onShowPlanDeviationsChange === 'function' && (
              <Form.Check
                type="checkbox"
                id="show-plan-deviations"
                label="Include Plan Deviations"
                checked={showPlanDeviations}
                onChange={(e) => onShowPlanDeviationsChange(e.target.checked)}
                style={{
                  color: colors.text,
                  marginBottom: 0,
                  whiteSpace: 'nowrap',
                  fontSize: '0.8rem',
                }}
              />
            )}

            {violations.length > 0 && (
              <Dropdown
                show={showFilterDropdown}
                onToggle={(isOpen) => setShowFilterDropdown(isOpen)}
              >
                <Dropdown.Toggle
                  as={Button}
                  variant="outline-custom"
                  size="sm"
                  style={{
                    backgroundColor: colors.bgInput || colors.bgSecondary,
                    border: `1px solid ${colors.borderLight || colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    padding: '6px 12px',
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    minWidth: '140px',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    {selectedRuleType === 'all'
                      ? `All Types (${violations.length})`
                      : `${getRuleDisplayName(selectedRuleType)} (${violations.filter((v) => v.rule_type === selectedRuleType).length})`}
                  </span>
                  <i className="fas fa-caret-down" style={{ marginLeft: '8px', fontSize: '0.75rem' }} />
                </Dropdown.Toggle>

                <Dropdown.Menu
                  align="start"
                  style={{
                    backgroundColor: colors.bgInput,
                    border: `1px solid ${colors.borderLight}`,
                    borderRadius: '8px',
                    padding: '8px 0',
                    marginTop: '4px',
                    minWidth: '180px',
                    maxWidth: '90vw',
                  }}
                >
                  <Dropdown.Item
                    onClick={() => {
                      if (onFilterChange) onFilterChange('all');
                      setShowFilterDropdown(false);
                    }}
                    style={{
                      color: selectedRuleType === 'all' ? colors.primary : colors.text,
                      backgroundColor: selectedRuleType === 'all' ? `${colors.primary}1A` : 'transparent',
                      padding: '8px 16px',
                      fontSize: '0.875rem',
                      fontWeight: selectedRuleType === 'all' ? '600' : '400',
                    }}
                  >
                    All Types ({violations.length})
                  </Dropdown.Item>
                  {availableRuleTypes.map((ruleType) => (
                    <Dropdown.Item
                      key={ruleType}
                      onClick={() => {
                        if (onFilterChange) onFilterChange(ruleType);
                        setShowFilterDropdown(false);
                      }}
                      style={{
                        color: selectedRuleType === ruleType ? colors.primary : colors.text,
                        backgroundColor: selectedRuleType === ruleType ? `${colors.primary}1A` : 'transparent',
                        padding: '8px 16px',
                        fontSize: '0.875rem',
                        fontWeight: selectedRuleType === ruleType ? '600' : '400',
                      }}
                    >
                      {getRuleDisplayName(ruleType)} ({violations.filter((v) => v.rule_type === ruleType).length})
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}

            <ButtonGroup size="sm">
              <Button
                variant={viewMode === 'total' ? 'primary' : 'outline-light'}
                onClick={() => setViewMode('total')}
                style={viewMode !== 'total' ? {
                  borderColor: colors.border,
                  color: colors.text,
                  backgroundColor: 'transparent',
                } : {}}
              >
                Total
              </Button>
              <Button
                variant={viewMode === 'byType' ? 'primary' : 'outline-light'}
                onClick={() => setViewMode('byType')}
                style={viewMode !== 'byType' ? {
                  borderColor: colors.border,
                  color: colors.text,
                  backgroundColor: 'transparent',
                } : {}}
              >
                Type
              </Button>
            </ButtonGroup>

            <ButtonGroup size="sm">
              <Button
                variant={chartStyle === 'line' ? 'primary' : 'outline-light'}
                onClick={() => setChartStyle('line')}
                style={chartStyle !== 'line' ? {
                  borderColor: colors.border,
                  color: colors.text,
                  backgroundColor: 'transparent',
                } : {}}
              >
                Line
              </Button>
              <Button
                variant={chartStyle === 'bar' ? 'primary' : 'outline-light'}
                onClick={() => setChartStyle('bar')}
                style={chartStyle !== 'bar' ? {
                  borderColor: colors.border,
                  color: colors.text,
                  backgroundColor: 'transparent',
                } : {}}
              >
                Bar
              </Button>
            </ButtonGroup>
          </div>
        )}

        {/* Chart + stats. Stats sit beside the plot on desktop and under it on mobile. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: aside && !isMobile ? 'minmax(0, 1fr) minmax(360px, 42%)' : '1fr',
            gap: isMobile ? 14 : 16,
            alignItems: 'stretch',
          }}
        >
          <div style={{ minWidth: 0 }}>
        {loading ? (
          <div
            aria-busy="true"
            style={{
              height: `${chartHeight}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        ) : hasNoViolations ? (
          <div style={{
            height: `${emptyStateHeight}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: isMobile ? '24px 16px' : '40px 20px'
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: colors.textLight,
              fontStyle: 'normal',
              textAlign: 'center'
            }}>
              Nice, no violations in the {getTimeRangeLabel()}!
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: colors.textMuted,
              fontWeight: 400,
              marginTop: '4px',
              textAlign: 'center'
            }}>
              Your trading activity stayed within all defined limits.
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            {chartStyle === 'line' ? (
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke={colors.textMuted}
                  style={{ fontSize: '0.75rem' }}
                  tick={{ fill: colors.textMuted }}
                  minTickGap={28}
                />
                <YAxis
                  stroke={colors.textMuted}
                  style={{ fontSize: '0.75rem' }}
                  tick={{ fill: colors.textMuted }}
                  label={{
                    value: showPlanDeviations ? 'Events' : 'Violations',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: colors.textMuted, fontSize: '0.7rem' },
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                {viewMode === 'total' ? (
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke={colors.primary}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ) : (
                  ruleTypesWithData.map((ruleType) => {
                    const color = getRuleTypeColor(ruleType, colors);
                    return (
                      <Line
                        key={ruleType}
                        type="monotone"
                        dataKey={ruleType}
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    );
                  })
                )}
              </LineChart>
            ) : viewMode === 'total' ? (
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke={colors.textMuted}
                  style={{ fontSize: '0.75rem' }}
                  tick={{ fill: colors.textMuted }}
                  minTickGap={28}
                />
                <YAxis
                  stroke={colors.textMuted}
                  style={{ fontSize: '0.75rem' }}
                  tick={{ fill: colors.textMuted }}
                  label={{
                    value: showPlanDeviations ? 'Events' : 'Violations',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: colors.textMuted, fontSize: '0.7rem' },
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="total"
                  fill={colors.primary}
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke={colors.textMuted}
                  style={{ fontSize: '0.75rem' }}
                  tick={{ fill: colors.textMuted }}
                  minTickGap={28}
                />
                <YAxis
                  stroke={colors.textMuted}
                  style={{ fontSize: '0.75rem' }}
                  tick={{ fill: colors.textMuted }}
                  label={{
                    value: showPlanDeviations ? 'Events' : 'Violations',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: colors.textMuted, fontSize: '0.7rem' },
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                {ruleTypesWithData.map((ruleType) => {
                  const color = getRuleTypeColor(ruleType, colors);
                  return (
                    <Bar
                      key={ruleType}
                      dataKey={ruleType}
                      fill={color}
                      stackId="violations"
                      radius={[2, 2, 0, 0]}
                    />
                  );
                })}
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
            {!loading && !hasNoViolations && (
              <ChartLegend items={legendItems} colors={colors} isMobile={isMobile} />
            )}
          </div>
          {aside ? (
            <div
              style={{
                minWidth: 0,
                paddingLeft: isMobile ? 0 : 22,
                paddingTop: isMobile ? 16 : 4,
                borderLeft: isMobile ? 'none' : `1px solid ${colors.border}`,
                borderTop: isMobile ? `1px solid ${colors.border}` : 'none',
              }}
            >
              {aside}
            </div>
          ) : null}
        </div>

        {/* Violations Table - Below chart (collapsible, expanded by default) */}
        {!hideTable && !loading && violations && violations.length > 0 && (
          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${colors.border}` }}>
            <button
              type="button"
              onClick={() => setTableExpanded((v) => !v)}
              aria-expanded={tableExpanded}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                background: 'none',
                border: 'none',
                padding: '0 0 12px',
                cursor: 'pointer',
                color: colors.textMuted,
              }}
            >
              <span
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 500,
                }}
              >
                Violations
                <span style={{ marginLeft: 8, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                  ({violations.length})
                </span>
              </span>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, fontStyle: 'italic' }}>
                {tableExpanded ? 'collapse' : 'expand'}
              </span>
            </button>
            {tableExpanded && (
              <ViolationsTable
                violations={violations}
                accountId={accountId}
                selectedRuleType={selectedRuleType}
                noCardWrapper={true}
                onViolationDeleted={onViolationDeleted}
              />
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ViolationsOverTimeChart;
